import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectTopLevelErrorFromState } from 'src/app/CreateBudget/redux/createBudget.selectors'
import {
  selectDeletingUserFromState,
  selectTopLevelErrorFromState as selectDeleteTopLevelErrorFromState,
} from 'src/app/ProfileDelete/redux/profileDelete.selectors'
import { selectEmailSendingFromState } from 'src/app/ResetPassword/redux/resetPassword.selectors'
import Loading from 'src/assets/images/Loading.svg'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ChangePasswordBox from 'src/components/ChangePasswordBox/ChangePasswordBox'
import DeleteProfileBox from 'src/components/DeleteProfileBox/DeleteProfileBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import TwoFactorAuthenticationProfileBox from 'src/components/TwoFactorAuthenticationProfileBox/TwoFactorAuthenticationProfileBox'
import UpdatePasswordBox from 'src/components/UpdatePasswordBox/UpdatePasswordBox'
import { AuthTypes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { translateSingleString } from 'src/localization/Trns'
import { postAuthType } from 'src/redux/actions/auth.actions'
import { deleteUser } from 'src/redux/actions/data/users.actions'
import {
  selectAuthTypeFromState,
  selectFetchingAuthTypeFromState,
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ProfileSecurity: React.FC = () => {
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  const authType = useSelector(selectAuthTypeFromState)
  const loading = useSelector(selectFetchingAuthTypeFromState)
  const emailSending = useSelector(selectEmailSendingFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const user = useSelector(selectUserListModelFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const appName = useSelector(selectAppName)

  const deletingUser = useSelector(selectDeletingUserFromState)
  const deleteTopLevelError = useSelector(selectDeleteTopLevelErrorFromState)

  useEffect(() => {
    if (user !== undefined) {
      dispatch(
        postAuthType({
          data: user.email,
        }),
      )
    }
  }, [user])

  const dispatchDeleteUserThenSignout = (): void => {
    dispatch(deleteUser(analytics))
  }

  return (
    <>
      <Helmet>
        <title>{`Security | ${appName}`}</title>
        <meta name="description" content={`Security page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent>
        {loading ? (
          <img
            css={css`
              height: 120px;
              width: 90px;
            `}
            alt={translateSingleString(locale, 'Loading')}
            src={Loading}
          />
        ) : authType === AuthTypes.cognito ? (
          <>
            <HorizontalContentCollection splitCenter>
              <ChangePasswordBox />
              <TwoFactorAuthenticationProfileBox hideOnOff />
            </HorizontalContentCollection>
            <div
              css={css`
                margin-top: 32px;
              `}
            >
              <DeleteProfileBox
                deleteProfile={dispatchDeleteUserThenSignout}
                deletingUser={deletingUser}
                topLevelError={deleteTopLevelError}
              />
            </div>
          </>
        ) : (
          <>
            <UpdatePasswordBox
              emailSending={emailSending}
              topLevelError={topLevelError}
              user={user}
            />
            <div
              css={css`
                margin-top: 32px;
              `}
            >
              <DeleteProfileBox
                deleteProfile={dispatchDeleteUserThenSignout}
                deletingUser={deletingUser}
                topLevelError={deleteTopLevelError}
              />
            </div>
          </>
        )}
      </BackgroundPageContent>
    </>
  )
}

export default ProfileSecurity
